import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import Link from '../../Link';
import Loader from '../../Loader';
import {
  fetchAdminInspectionReservation, fetchInspectionComparison, fetchDispute, decideDispute, changeDisputeStatus
} from '../../../actions/Inspection/inspection';
import { demoRestriction } from '../../../helpers/DemoRestriction/demoRestriction';
import { FORM_LIMITS, validateDecision, buildDecisionArgs, previewBalance } from '../../../helpers/inspectionForm';
import { disputeStatusKey, decisionLabelKey, globalStatusKey, globalStatusTone } from '../../../helpers/inspectionPresentation';
import { prepareAttachment } from '../../../helpers/inspectionUpload';
import usePhotoSeries from '../../Inspection/usePhotoSeries';
import InspectionBanner from '../../Inspection/InspectionBanner/InspectionBanner';
import InspectionStatusBadge from '../../Inspection/InspectionStatusBadge/InspectionStatusBadge';
import { AttachmentPicker } from '../../Inspection/DisputePanel/DisputePanel';
import InspectionTimeline from '../InspectionAdmin/InspectionTimeline';
import PhotoComparison from '../InspectionAdmin/PhotoComparison';
import ins from '../../Inspection/Inspection.css';
import s from '../InspectionAdmin/InspectionAdmin.css';

const ROLE_KEYS = { host: 'inspRoleHost', guest: 'inspRoleGuest', admin: 'inspRoleAdmin' };
const STATUS_ACTIONS = { open: ['in_review'], in_review: ['open'], resolved: ['closed'] };
const STATUS_ACTION_KEYS = { in_review: 'inspAdmStatusToReview', open: 'inspAdmStatusToOpen', closed: 'inspAdmStatusToClosed' };

// Dispute screen of a reservation (R15 to R21): reservation and timeline, dispute details, deposit block (amount and status, or "no
// deposit" without any action, R19), departure / damage photos, statements of both parties, and the administrator decision with a
// mandatory reason (R20). The decision only records the outcome and the amounts computed by the server: it moves no money. The
// refund of the renter and the payment of the owner stay in the existing deposit screens (linked below).
export function DisputeManagement({ reservationId }) {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true });
  const [type, setType] = useState('');
  const [retained, setRetained] = useState('');
  const [requested, setRequested] = useState('');
  const [motive, setMotive] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const [reservation, dispute, comparison] = await Promise.all([
      dispatch(fetchAdminInspectionReservation(reservationId)),
      dispatch(fetchDispute(reservationId)),
      dispatch(fetchInspectionComparison(reservationId))
    ]);
    setState({
      loading: false,
      reservation: reservation && reservation.id ? reservation : null,
      dispute: dispute && dispute.status === 'success' ? dispute.dispute : null,
      comparison: comparison && comparison.status === 'success' ? comparison : null,
      error: !reservation || !reservation.id || !dispute || dispute.status !== 'success' ? (dispute && dispute.errorMessage) || true : null
    });
  }, [dispatch, reservationId]);

  useEffect(() => { load(); }, [load]);

  const series = usePhotoSeries({
    reservationId, type: 'attachment', kind: 'attachment', initial: [], prepare: prepareAttachment,
    onError: (failure) => setNotice({ tone: 'danger', text: failure.serverMessage || formatMessage(messages[failure.messageKey]) })
  });

  if (state.loading) return <Loader type="text" />;
  const { reservation, dispute, comparison } = state;
  if (state.error || !reservation) return <InspectionBanner tone="danger" title={typeof state.error === 'string' ? state.error : formatMessage(messages.inspNotFound)} />;
  if (!dispute) return (
    <div>
      <Link to="/siteadmin/reservations" className={ins.backLink}>{'← '}{formatMessage(messages.inspAdmBackToReservations)}</Link>
      <InspectionBanner tone="info" title={formatMessage(messages.inspAdmNoDispute)} />
    </div>
  );

  const deposit = Number(reservation.securityDeposit) || 0;
  const hasDeposit = deposit > 0;
  const currency = reservation.currency || '';
  const money = (value) => `${Number(value || 0).toLocaleString(locale)} ${currency}`;
  const when = (value) => (value ? new Date(value).toLocaleString(locale) : '');
  const active = Boolean(dispute.isActive);
  const decision = (dispute.decisions || [])[0] || null;
  const options = hasDeposit ? ['release_all', 'retain_part', 'retain_all'] : ['no_action', 'request_indemnity'];
  const selected = options.includes(type) ? type : '';
  const balance = selected === 'retain_part' ? previewBalance(deposit, Number(retained)) : null;
  const tone = globalStatusTone(comparison ? comparison.globalStatus : 'dispute');
  const checkin = comparison ? comparison.checkin : null;
  const checkout = comparison ? comparison.checkout : null;

  const optionHelp = (key) => {
    if (key === 'release_all') return formatMessage(messages.inspAdmReleaseHelp, { amount: money(deposit) });
    if (key === 'retain_all') return formatMessage(messages.inspAdmRetainAllHelp, { amount: money(deposit) });
    if (key === 'no_action') return formatMessage(messages.inspAdmNoActionHelp);
    return null;
  };

  const askConfirm = () => {
    setNotice(null);
    const error = validateDecision({ type: selected, motive, retainedAmount: retained, requestedAmount: requested, deposit });
    if (error) { setNotice({ tone: 'danger', text: formatMessage(messages[error]) }); return; }
    setConfirming(true);
  };

  const submitDecision = async () => {
    setBusy(true); setNotice(null);
    const restricted = await dispatch(demoRestriction({}));
    if (restricted) { setBusy(false); setConfirming(false); return; }
    const args = buildDecisionArgs({ disputeId: dispute.id, type: selected, motive, retainedAmount: retained, requestedAmount: requested, attachments: series.readyItems.map((item) => item.id) });
    const response = await dispatch(decideDispute(args));
    if (response && response.status === 'success') {
      // The amounts shown afterwards are the ones computed and stored by the server.
      setResult(response);
      setConfirming(false);
      await load();
    } else {
      setConfirming(false);
      setNotice({ tone: 'danger', text: (response && response.errorMessage) || formatMessage(messages.inspErrGeneric) });
    }
    setBusy(false);
  };

  const changeStatus = async (status) => {
    setBusy(true); setNotice(null);
    const response = await dispatch(changeDisputeStatus({ disputeId: dispute.id, status }));
    if (response && response.status === 'success') await load();
    else setNotice({ tone: 'danger', text: (response && response.errorMessage) || formatMessage(messages.inspErrGeneric) });
    setBusy(false);
  };

  return (
    <div>
      <Link to="/siteadmin/reservations" className={ins.backLink}>{'← '}{formatMessage(messages.inspAdmBackToReservations)}</Link>
      <div className={s.pageHead}>
        <div>
          <h1 className={s.headTitle}>
            {formatMessage(messages.inspAdmReservationNumber, { id: reservation.id })}{' '}
            <InspectionStatusBadge entry={{ labelKey: globalStatusKey(comparison ? comparison.globalStatus : 'dispute'), tone, icon: tone === 'danger' ? 'alert' : tone === 'success' ? 'ok' : 'clock' }} />
          </h1>
          <p className={s.headMeta}>
            {formatMessage(messages.inspAdmCreatedOn, { date: when(reservation.createdAt) })}
            {reservation.confirmationCode ? ` - ${formatMessage(messages.inspAdmCode, { code: reservation.confirmationCode })}` : ''}
          </p>
        </div>
        <Link to={`/siteadmin/viewreservation/${reservation.id}/reservation`} className={ins.entryLink}>{formatMessage(messages.inspAdmSeeReservation)}</Link>
      </div>

      <section className={ins.card}>
        <div className={ins.summary} style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr' }}>
          <div><p className={ins.summaryLabel}>{formatMessage(messages.inspAdmVehicle)}</p><p className={ins.summaryName}>{reservation.listData ? reservation.listData.title : '-'}</p></div>
          <div><p className={ins.summaryLabel}>{formatMessage(messages.inspSummaryRenter)}</p><p className={ins.summaryValue}>{reservation.guestData ? <Link to={`/users/show/${reservation.guestData.profileId}`}>{reservation.guestData.firstName}</Link> : '-'}</p></div>
          <div><p className={ins.summaryLabel}>{formatMessage(messages.inspSummaryOwner)}</p><p className={ins.summaryValue}>{reservation.hostData ? <Link to={`/users/show/${reservation.hostData.profileId}`}>{reservation.hostData.firstName}</Link> : '-'}</p></div>
          <div><p className={ins.summaryLabel}>{formatMessage(messages.inspAdmTotal)}</p><p className={ins.summaryValue}>{money(reservation.total)}</p></div>
        </div>
      </section>

      <section className={ins.card}><InspectionTimeline reservation={reservation} checkin={checkin} checkout={checkout} dispute={dispute} /></section>

      {notice && <InspectionBanner tone={notice.tone}>{notice.text}</InspectionBanner>}
      {result && (
        <InspectionBanner tone="success" title={formatMessage(messages.inspAdmDecisionSaved)}>
          {formatMessage(messages.inspAdmDecisionSavedDetail, { retained: money(result.retainedAmount), refund: money(result.refundAmount), requested: money(result.requestedAmount) })}
        </InspectionBanner>
      )}

      <div className={s.decisionGrid}>
        <div>
          <section className={ins.card}>
            <h2 className={ins.cardTitle}>{formatMessage(messages.inspAdmDisputeDetails)}</h2>
            <InspectionBanner tone={active ? 'danger' : 'success'} title={`${formatMessage(messages[disputeStatusKey(dispute.status)])}`}>
              {formatMessage(messages.inspAdmDisputeOpenedBy, { role: formatMessage(messages[ROLE_KEYS[dispute.openedByRole] || 'inspRoleGuest']), date: when(dispute.createdAt) })}
            </InspectionBanner>
            <p className={ins.infoStrip}>{formatMessage(messages.inspDisputeIntermediary)}</p>
            {(STATUS_ACTIONS[dispute.status] || []).length > 0 && (
              <div className={ins.actions}>
                {STATUS_ACTIONS[dispute.status].map((status) => (
                  <button key={status} type="button" className={cx(ins.button, ins.buttonSecondary)} disabled={busy} onClick={() => changeStatus(status)}>{formatMessage(messages[STATUS_ACTION_KEYS[status]])}</button>
                ))}
              </div>
            )}
          </section>

          <section className={ins.card}>
            <h2 className={ins.cardTitle}>{formatMessage(messages.inspAdmPhotosTitle)}</h2>
            <PhotoComparison departure={checkin ? checkin.photos : []} damage={checkout && checkout.hasDamage ? checkout.photos : []} />
          </section>

          <section className={ins.card}>
            <h2 className={ins.cardTitle}>{formatMessage(messages.inspAdmCommentsTitle)}</h2>
            <ul className={ins.thread}>
              {(dispute.statements || []).map((item) => (
                <li key={item.id} className={ins.statement}>
                  <p className={ins.statementHead}><span>{formatMessage(messages[ROLE_KEYS[item.role] || 'inspRoleGuest'])}{item.isInternal ? ` (${formatMessage(messages.inspAdmInternal)})` : ''}</span><span>{when(item.createdAt)}</span></p>
                  <p className={ins.statementBody}>{item.content}</p>
                  {item.attachments && item.attachments.length > 0 && (
                    <ul className={ins.attachmentList}>
                      {item.attachments.map((id, index) => <li key={id}><a className={ins.attachmentLink} href={`/inspection/photo/${id}`} target="_blank" rel="noopener noreferrer">{formatMessage(messages.inspAttachmentN, { index: index + 1 })}</a></li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div>
          <section className={ins.card}>
            <h2 className={ins.cardTitle}>{formatMessage(messages.inspAdmDepositTitle)}</h2>
            {hasDeposit ? (
              <>
                <p className={s.depositAmount}>{money(deposit)}</p>
                <p className={ins.cardHint}>
                  {decision && decision.refundAmount !== undefined && decision.type !== 'no_action' && decision.type !== 'request_indemnity'
                    ? formatMessage(messages.inspAdmDepositDecided)
                    : formatMessage(messages.inspAdmDepositHeld)}
                </p>
              </>
            ) : (
              <InspectionBanner tone="info" title={formatMessage(messages.inspAdmNoDepositTitle)}>{formatMessage(messages.inspAdmNoDepositHint)}</InspectionBanner>
            )}
          </section>

          {decision && (
            <section className={ins.card}>
              <h2 className={ins.cardTitle}>{formatMessage(messages.inspDecisionOfAdmin)}</h2>
              <p className={ins.summaryValue}>{formatMessage(messages[decisionLabelKey(decision.type)])}</p>
              {decision.retainedAmount > 0 && <p>{formatMessage(messages.inspDecisionRetained)}: <strong>{money(decision.retainedAmount)}</strong></p>}
              {hasDeposit && decision.type !== 'no_action' && decision.type !== 'request_indemnity' && <p>{formatMessage(messages.inspAdmBalanceToRenter)}: <strong>{money(decision.refundAmount)}</strong></p>}
              {decision.requestedAmount > 0 && <p>{formatMessage(messages.inspDecisionRequested)}: <strong>{money(decision.requestedAmount)}</strong></p>}
              <p className={ins.statementBody}>{decision.motive}</p>
              <p className={ins.cardHint}>{when(decision.createdAt)}</p>
              <InspectionBanner tone="warning" title={formatMessage(messages.inspAdmNoMoneyMovedTitle)}>{formatMessage(messages.inspAdmNoMoneyMoved)}</InspectionBanner>
              <div className={ins.actions}>
                {hasDeposit && <Link to="/siteadmin/manage-security-deposit" className={ins.entryLink}>{formatMessage(messages.inspAdmGoDeposit)}</Link>}
                <Link to="/siteadmin/payout" className={ins.entryLink}>{formatMessage(messages.inspAdmGoPayout)}</Link>
              </div>
            </section>
          )}

          {active && (
            <section className={ins.card} aria-label={formatMessage(messages.inspDecisionOfAdmin)}>
              <h2 className={ins.cardTitle}>{formatMessage(messages.inspDecisionOfAdmin)}</h2>
              <form noValidate onSubmit={(event) => event.preventDefault()}>
                {options.map((key) => (
                  <label key={key} className={cx(s.optionBox, selected === key && s.optionBoxSelected)}>
                    <input type="radio" name="dispute-decision" value={key} checked={selected === key} onChange={() => { setType(key); setConfirming(false); }} />{' '}
                    <span className={s.optionName}>{formatMessage(messages[decisionLabelKey(key)])}</span>
                    {optionHelp(key) && <span className={s.optionHelp}>{optionHelp(key)}</span>}
                    {key === 'retain_part' && selected === key && (
                      <>
                        <span className={s.amountRow}>
                          <input type="number" min="0" step="any" className={ins.input} value={retained} aria-label={formatMessage(messages.inspDecisionRetained)} onChange={(event) => setRetained(event.target.value)} />
                          <span className={s.currency}>{currency}</span>
                        </span>
                        <span className={s.balance}>{formatMessage(messages.inspAdmBalancePreview)}: {balance === null ? '-' : money(balance)}</span>
                      </>
                    )}
                    {key === 'request_indemnity' && selected === key && (
                      <>
                        <span className={s.amountRow}>
                          <input type="number" min="0" step="any" className={ins.input} value={requested} placeholder={formatMessage(messages.inspDecisionRequested)} aria-label={formatMessage(messages.inspDecisionRequested)} onChange={(event) => setRequested(event.target.value)} />
                          <span className={s.currency}>{currency}</span>
                        </span>
                        <span className={s.optionHelp}>{formatMessage(messages.inspAdmIndemnityHelp)}</span>
                      </>
                    )}
                  </label>
                ))}
                <div className={ins.field}>
                  <label className={ins.fieldLabel} htmlFor="decision-motive">{formatMessage(messages.inspAdmMotive)}<span className={ins.required} aria-hidden="true"> *</span></label>
                  <textarea id="decision-motive" className={ins.textarea} value={motive} maxLength={FORM_LIMITS.motive} placeholder={formatMessage(messages.inspAdmMotivePlaceholder)} aria-required="true" onChange={(event) => setMotive(event.target.value)} />
                  <p className={ins.charCount}>{motive.length}/{FORM_LIMITS.motive}</p>
                </div>
                <AttachmentPicker reservationId={reservationId} series={series} label={formatMessage(messages.inspAdmSupportingDocs)} />
                {confirming ? (
                  <InspectionBanner tone="warning" title={formatMessage(messages.inspAdmConfirmTitle)}>{formatMessage(messages.inspAdmConfirmText)}</InspectionBanner>
                ) : null}
                <div className={ins.actions}>
                  <button type="button" className={cx(ins.button, ins.buttonSecondary)} disabled={busy} onClick={() => { if (confirming) setConfirming(false); else { setType(''); setRetained(''); setRequested(''); setMotive(''); series.reset([]); setNotice(null); } }}>{formatMessage(messages.inspCancel)}</button>
                  {confirming
                    ? <button type="button" className={ins.button} disabled={busy || series.busy} onClick={submitDecision}>{formatMessage(messages.inspAdmConfirmButton)}</button>
                    : <button type="button" className={ins.button} disabled={busy || series.busy || !selected} onClick={askConfirm}>{formatMessage(messages.inspAdmValidateDecision)}</button>}
                </div>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

DisputeManagement.propTypes = { reservationId: PropTypes.number.isRequired };

export default withStyles(ins, s)(DisputeManagement);
